import { Button, Grid, Group, Layout, Page } from "@/comn/components";
import { useAuth, useFetch, useForm, useGrid, useModal, usePage, useResource, useStore, useTheme } from "@/comn/hooks";
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ptlUtils } from "@/tz/go/egaz/osci/ptl/comn";
import {
    APIS,
    SF_OGA_USR_SRCH,
    SG_OGA_USR_LIST,
    URLS,
} from "@/tz/go/egaz/osci/ptl/ptl/usrauth/services/OsciOgaAuthService";

/*
 * OGA User Auth Management List
 * !== OGA 사용자 권한 관리 목록 ==!
 */
export const OSCI0104020Q = (): JSX.Element => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_osci0003", "auth_osci0002"],
    });
    const modal = useModal();
    const { theme } = useTheme();
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid });
    const auth = useAuth();

    // auth_osci0003(OSC 관리자) 권한 보유 여부 !== 미보유 시 조회 조건 orgCd 를 본인 조직으로 고정 ==!
    const hasOscAdminAuth = ptlUtils.hasAuth("auth_osci0003", auth);

    useResource({ defaultSchema: [{ area: "orgCd" }] });

    const form = {
        ogaUsrSrch: useForm({
            defaultSchema: SF_OGA_USR_SRCH,
            defaultValues: {
                // 최초 요청부터 orgCd 가 실려야 useFetch 자동조회와의 경합을 피할 수 있음
                ...(!hasOscAdminAuth ? { orgCd: auth.get("orgCd") } : {}),
            },
        }),
    };

    const grid = {
        gridOgaUsrList: useGrid({
            defaultSchema: SG_OGA_USR_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    const fetch = {
        getOgaUsrList: useFetch({
            api: (page = grid.gridOgaUsrList.page, size = grid.gridOgaUsrList.size) =>
                APIS.getOgaUsrList(form.ogaUsrSrch.getValues(), page, size),
            enabled: form.ogaUsrSrch.isSubmitted,
            key: [grid.gridOgaUsrList.page, grid.gridOgaUsrList.size],
            onSuccess: () => {
                setStore(pgeUid, {
                    ...pgeStore,
                    form: form.ogaUsrSrch.getValues(),
                    page: grid.gridOgaUsrList.page,
                    size: grid.gridOgaUsrList.size,
                });
            },
            onError: () => modal.openModal({ content: "msg.osc.00043" }), // Failed to select. !== 조회가 실패하였습니다. ==!
        }),
    };

    const render = {
        ogaUsrList: {
            cell: {
                usrNm: (props: any) => {
                    const { value, rowValues } = props;
                    return <Link to={`${URLS.osci0104021s}/${rowValues.tin}`}>{value}</Link>;
                },
            },
        },
    };

    const handler = {
        onSrchClick: form.ogaUsrSrch.handleSubmit(() => fetch.getOgaUsrList.fetch()),
        onClear: () => {
            form.ogaUsrSrch.clearValues();

            if (!hasOscAdminAuth) {
                form.ogaUsrSrch.setValue("orgCd", auth.get("orgCd"));
                form.ogaUsrSrch.setSchema("orgCd", { edit: false });
            }

            grid.gridOgaUsrList.getData().length > 0 && grid.gridOgaUsrList.clearData();
        },
    };

    useEffect(() => {
        pgeStore?.form && form.ogaUsrSrch.setValues(pgeStore.form);

        if (!hasOscAdminAuth) {
            form.ogaUsrSrch.setValue("orgCd", auth.get("orgCd"));
            form.ogaUsrSrch.setSchema("orgCd", { edit: false });
        }

        pgeStore?.form && handler.onSrchClick();
    }, [theme]);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.ogaUsrSrch.schema.orgCd} />
                            <Group.Control {...form.ogaUsrSrch.schema.tin} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.ogaUsrSrch.schema.usrNm} />
                        </Group.Row>
                    </Group.Section>
                    <Layout>
                        <Layout.Left>
                            <Button role={"reset"} onClick={handler.onClear} />
                        </Layout.Left>
                        <Layout.Right>
                            <Button role={"search"} onClick={handler.onSrchClick} />
                        </Layout.Right>
                    </Layout>
                </Group.Body>
            </Group>

            <Group>
                <Group.Body>
                    <Group.Title title={"T_OGA_USR_LIST"} />
                    <Grid
                        {...grid.gridOgaUsrList.grid}
                        data={fetch.getOgaUsrList.data?.ogaUsrList}
                        render={render.ogaUsrList}
                    />
                </Group.Body>
            </Group>
        </Page>
    );
};
